import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, X, MessageCircle, Trash2, MapPin } from "lucide-react";
import { useLang } from "@/i18n";
import { waLink } from "@/api";
import { ATTRACTIONS } from "@/staticData";
import { NIGHTLIFE } from "@/nightlifeData";
import { useFavorites } from "@/favorites";

const ALL_PLACES = [...ATTRACTIONS, ...NIGHTLIFE];
const placeName = (p, lang) => p[`name_${lang}`] || p.name_en || p.name;

export default function MyTransylvania({ config }) {
  const { t, lang } = useLang();
  const { favs, toggle, clear } = useFavorites();
  const [open, setOpen] = useState(false);
  const wa = config?.whatsapp || "40721663181";

  const places = favs.map((id) => ALL_PLACES.find((p) => p.id === id)).filter(Boolean);
  const list = places.map((p) => `• ${placeName(p, lang)}`).join("\n");
  const waMsg = t("fav.wa_msg").replace("{list}", list);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="fav-open"
        aria-label={t("fav.button")}
        className="fixed bottom-6 left-6 z-[60] flex items-center gap-2 rounded-full bg-main text-cream-light pl-4 pr-5 py-3 shadow-xl shadow-main/30 hover:bg-terracotta transition-colors duration-300"
      >
        <Heart size={17} className={favs.length ? "fill-terracotta text-terracotta" : ""} />
        <span className="text-sm font-semibold hidden sm:inline">{t("fav.button")}</span>
        {favs.length > 0 && (
          <span className="min-w-5 h-5 rounded-full bg-terracotta text-cream-light text-[11px] font-bold flex items-center justify-center px-1" data-testid="fav-count">
            {favs.length}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-main/60 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="fav-overlay">
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-0 top-0 h-full w-full max-w-md bg-cream shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
              data-testid="fav-drawer"
            >
              <div className="flex items-center justify-between p-6 border-b border-warm-grey/40">
                <h3 className="font-serif text-2xl text-main flex items-center gap-2">
                  <Heart size={20} className="text-terracotta fill-terracotta" /> {t("fav.title")}
                </h3>
                <button onClick={() => setOpen(false)} data-testid="fav-close" aria-label="Close"
                  className="w-9 h-9 rounded-full bg-cream-light border border-warm-grey/50 flex items-center justify-center text-brown hover:text-terracotta transition-colors">
                  <X size={16} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {places.length === 0 ? (
                  <p className="text-sm text-brown/70 font-light leading-relaxed" data-testid="fav-empty">{t("fav.empty")}</p>
                ) : (
                  <>
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-walnut" data-testid="fav-count-label">
                      {t("fav.count").replace("{n}", places.length)}
                    </p>
                    <ul className="mt-4 space-y-3">
                      {places.map((p) => (
                        <li key={p.id} className="flex items-center justify-between gap-3 bg-cream-light border border-warm-grey/40 rounded-2xl px-4 py-3" data-testid={`fav-item-${p.id}`}>
                          <span className="flex items-center gap-2.5 text-sm text-main font-medium">
                            <MapPin size={14} className="text-terracotta shrink-0" /> {placeName(p, lang)}
                          </span>
                          <button onClick={() => toggle(p.id)} data-testid={`fav-remove-${p.id}`} aria-label={t("attx.fav_remove")}
                            className="text-warm-grey hover:text-terracotta transition-colors">
                            <X size={15} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              {places.length > 0 && (
                <div className="p-6 border-t border-warm-grey/40 space-y-3">
                  <a href={waLink(wa, waMsg)} target="_blank" rel="noreferrer" data-testid="fav-send-wa"
                    className="flex items-center justify-center gap-2 w-full rounded-full bg-terracotta text-cream-light font-semibold px-6 py-3.5 hover:bg-terracotta/90 transition-all duration-300">
                    <MessageCircle size={17} /> {t("fav.cta")}
                  </a>
                  <button onClick={clear} data-testid="fav-clear"
                    className="flex items-center justify-center gap-2 w-full text-sm text-brown/60 hover:text-terracotta transition-colors">
                    <Trash2 size={14} /> {t("fav.clear")}
                  </button>
                </div>
              )}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
